// Guest detail (spec §6.5): GDPR export/delete via admin-user-data.
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { callEdgeFunction } from '@/lib/api/edgeFunction';
import { adminMutate } from '@/lib/api/adminMutate';
import { toReason } from '@/lib/api/reason';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/guests/$id')({
  component: GuestDetail,
});

interface PreviewResponse {
  user: { id: string; email: string | null; created_at: string | null };
  facts: Record<string, unknown>;
  // Top-level, not nested in `facts` (admin-user-data's actual preview
  // response, confirmed by reading the function post-3c8b4b2 — the
  // original guess at this shape had it inside `facts`, which was wrong).
  has_upcoming_bookings: boolean;
  plan: { mode: string; error?: string };
}

function useGuestPreview(userId: string) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['admin-user-data', 'preview', userId],
    queryFn: () => callEdgeFunction<PreviewResponse>(supabase, 'admin-user-data', { action: 'preview', userId }),
  });
}

function GuestDetail() {
  const { id } = Route.useParams();
  const { supabase } = useAuth();
  const preview = useGuestPreview(id);

  const [reason, setReason] = useState('');
  const [confirmUpcoming, setConfirmUpcoming] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [deleteResult, setDeleteResult] = useState<string | null>(null);

  const exportMutation = useMutation({
    mutationFn: async () => {
      const r = toReason(reason);
      if (!r) throw new Error('A reason of at least 10 characters is required.');
      // export-user-data returns an attachment body, not JSON — reuse the
      // same fetch-and-download shape as the dispute kit rather than
      // callEdgeFunction (which assumes a JSON response).
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
      if (!token || !supabaseUrl || !anonKey) throw new Error('Not signed in.');
      const res = await fetch(`${supabaseUrl}/functions/v1/admin-user-data`, {
        method: 'POST',
        // apikey required alongside Authorization (fix round 1, item 8) —
        // Kong's local gateway routes on it even with a Bearer token present.
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: anonKey },
        body: JSON.stringify({ action: 'export', userId: id, reason: r }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? body?.error ?? `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `namibiago-data-export-${id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (err) => setExportError(err instanceof Error ? err.message : String(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (mode: 'erase' | 'anonymise') => {
      const r = toReason(reason);
      if (!r) throw new Error('A reason of at least 10 characters is required.');
      return adminMutate<{ outcome: string }>(
        supabase,
        'admin-user-data',
        { action: 'delete', userId: id, mode, confirmUpcoming },
        r,
      );
    },
    onSuccess: (res) => setDeleteResult(res.outcome),
  });

  if (preview.isLoading) return <PageLoading />;
  if (preview.isError || !preview.data) {
    return <p className="text-body text-danger">Couldn't load this guest: {(preview.error as Error)?.message}</p>;
  }

  const { user, facts, plan, has_upcoming_bookings: hasUpcoming } = preview.data;
  // `plan` is always computed for mode 'erase' with confirmUpcoming=false
  // (the preview endpoint's own fixed call) — it can't reflect a checkbox
  // ticked after the preview loaded, so `has_upcoming_bookings` is handled
  // separately via `canProceed` rather than baked into `plan.error`.
  // `owns_business` blocks both erase and anonymise; `retention_hold` only
  // blocks a plain erase (anonymise is exactly the answer to it).
  const canProceed = !hasUpcoming || confirmUpcoming;
  const eraseBlocked = plan.error === 'owns_business' || plan.error === 'retention_hold';
  const anonymiseBlocked = plan.error === 'owns_business';

  return (
    <section className="flex flex-col gap-lg">
      <div>
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">{user.email ?? user.id}</h1>
        <p className="mt-xs text-body text-ink-2">Account created {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</p>
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Facts</h2>
        <dl className="mt-md grid grid-cols-2 gap-y-sm text-body">
          {Object.entries(facts).map(([k, v]) => (
            <Fragment key={k}>
              <dt className="text-ink-2">{k.replace(/_/g, ' ')}</dt>
              <dd className="text-ink">{String(v)}</dd>
            </Fragment>
          ))}
        </dl>
        {plan.error ? <p className="mt-sm text-body text-danger">A delete would be refused: {plan.error}</p> : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <label className="text-caption font-semibold text-ink-2" htmlFor="guest-reason">
          Reason (required — logged to admin_actions before the export/erasure runs)
        </label>
        <textarea
          id="guest-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-xs min-h-[52px] w-full rounded-button border border-border bg-surface p-sm text-body text-ink"
        />

        {hasUpcoming ? (
          <label className="mt-md flex items-center gap-sm text-body text-ink">
            <input type="checkbox" checked={confirmUpcoming} onChange={(e) => setConfirmUpcoming(e.target.checked)} />
            This guest has upcoming bookings — I confirm proceeding is still correct.
          </label>
        ) : null}

        <div className="mt-md flex flex-wrap gap-sm">
          <button
            type="button"
            disabled={exportMutation.isPending || !toReason(reason)}
            onClick={() => exportMutation.mutate()}
            className="rounded-button border border-border px-md py-sm text-body text-ink disabled:opacity-50"
          >
            Export GDPR data
          </button>
          <button
            type="button"
            disabled={deleteMutation.isPending || !toReason(reason) || eraseBlocked || !canProceed}
            onClick={() => deleteMutation.mutate('erase')}
            className="rounded-button bg-danger px-md py-sm text-body font-semibold text-white disabled:opacity-50"
          >
            Erase account
          </button>
          <button
            type="button"
            disabled={deleteMutation.isPending || !toReason(reason) || anonymiseBlocked || !canProceed}
            onClick={() => deleteMutation.mutate('anonymise')}
            className="rounded-button border border-danger px-md py-sm text-body font-semibold text-danger disabled:opacity-50"
          >
            Anonymise (keeps bookings/payments/refunds)
          </button>
        </div>
        {exportError ? <p className="mt-sm text-body text-danger">{exportError}</p> : null}

        {deleteResult ? <p className="mt-sm text-body text-ink">Outcome: {deleteResult}</p> : null}
        {deleteMutation.isError ? (
          <p className="mt-sm text-body text-danger">{(deleteMutation.error as Error).message}</p>
        ) : null}
      </div>
    </section>
  );
}
